import { Popover, Switch, Text, Toast, Tooltip } from '@byecode/ui'
import { disableWorkflowErrorMessage, enableWorkflowErrorMessage, TooltipText, useAtomAction, useAtomData } from '@lighthouse/shared'
import { lightFormat } from 'date-fns'
import { find } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'

import { workflowAtomFamily, workflowListAtom } from '@/atoms/workflow/state'
import { workflowLabelMap } from '@/constants/flow'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import * as srv from '@/services'

import { SettingPopoverContent } from '../SettingPopoverContent'
import * as SC from './styles'

interface FlowCardProps {
    id: string
    onClick: () => void
    onDelete: () => void
    onDuplicate: () => void
}

export const FlowCard: React.FC<FlowCardProps> = ({ id, onClick, onDelete, onDuplicate }) => {
    const workflow = useAtomData(
        workflowListAtom,
        useCallback(draft => find(item => item.id === id, draft), [id])
    )!
    const { run: updateWorkflow } = useAtomAction(workflowAtomFamily(id))
    const { type, description, name, unConfigured, updatedTime, updatedBy, createdTime, createdBy, enabled } = workflow
    const disabledWithVersion = useIsDisabledWithVersion()
    const { color, icon } = workflowLabelMap[type]

    const [open, setOpen] = useState(false)
    const descriptions = useMemo(
        () => [
            { id: '1', label: '最后编辑', value: lightFormat(updatedTime ?? Date.now(), 'yyyy/MM/dd HH:mm') },
            { id: '2', label: '编辑人', value: updatedBy || '' },
            { id: '3', label: '创建时间', value: lightFormat(createdTime ?? Date.now(), 'yyyy/MM/dd HH:mm') },
            { id: '4', label: '创建人', value: createdBy || '' }
        ],
        [createdBy, createdTime, updatedBy, updatedTime]
    )

    const handleFlowNameChange = useCallback(
        async (val: string) => {
            const isUpdate = await srv.updateFlow({ id, name: val })
            if (isUpdate) {
                updateWorkflow(draft => {
                    if (!draft) {
                        return
                    }
                    draft.name = val
                })
            }
        },
        [id, updateWorkflow]
    )
    const handleFlowDescriptionChange = useCallback(
        async (val: string) => {
            const isUpdate = await srv.updateFlow({ id, description: val })
            if (isUpdate) {
                updateWorkflow(draft => {
                    if (!draft) {
                        return
                    }
                    draft.description = val
                })
            }
        },
        [id, updateWorkflow]
    )

    const handleFlowEnabledChange = useCallback(
        async (enabled: boolean) => {
            if (unConfigured) {
                Toast.error(enabled ? enableWorkflowErrorMessage : disableWorkflowErrorMessage)
                return
            }
            try {
                const isUpdate = await srv.enableFlow(id, enabled)
                if (!isUpdate) {
                    Toast.error(enabled ? enableWorkflowErrorMessage : disableWorkflowErrorMessage)
                    return
                }
                updateWorkflow(draft => {
                    if (!draft) {
                        return
                    }
                    draft.enabled = enabled
                })
                Toast.success(`${enabled ? '启用' : '禁用'}成功`)
            } catch {
                Toast.error(enabled ? enableWorkflowErrorMessage : disableWorkflowErrorMessage)
            }
        },
        [id, unConfigured, updateWorkflow]
    )

    const handleDelete = useCallback(() => {
        setOpen(false)
        onDelete()
    }, [onDelete])

    const handleDuplicate = useCallback(() => {
        setOpen(false)
        onDuplicate()
    }, [onDuplicate])

    return (
        <>
            <SC.FlowCardWrapper onClick={onClick}>
                <SC.FlowCardContent>
                    <SC.FlowCardHeader>
                        <SC.FlowCardIconsWrapper>
                            <SC.Header>
                                <SC.HeaderIcon>
                                    <SC.FlowCardTriggerIcon
                                        size={20}
                                        type={icon}
                                        fill="var(--color-white)"
                                        style={{ backgroundColor: color }}
                                    />
                                </SC.HeaderIcon>
                                <SC.HeaderText>
                                    <TooltipText
                                        title={name}
                                        render={ref => (
                                            <Text color="var(--color-gray-900)" ref={ref}>
                                                {name}
                                            </Text>
                                        )}
                                    />

                                    <SC.HeaderTextType>{workflowLabelMap[type].label}</SC.HeaderTextType>
                                </SC.HeaderText>
                            </SC.Header>
                            {/* <SC.FlowCardArrowIcon type="ArrowRight" size={20} /> */}
                            {/* {extraNodeContent} */}
                        </SC.FlowCardIconsWrapper>
                        <SC.FlowCardExtraLinearBackground />
                        {!disabledWithVersion && (
                            <SC.FlowCardActionWrapper onClick={ev => ev.stopPropagation()}>
                                <Popover withinPortal width={220} opened={open} onChange={setOpen}>
                                    <Popover.Target>
                                        <SC.FlowCardAction type="DotsThree" />
                                    </Popover.Target>
                                    <Popover.Dropdown>
                                        <SettingPopoverContent
                                            name={name}
                                            description={description}
                                            descriptions={descriptions}
                                            onDelete={handleDelete}
                                            onDuplicate={handleDuplicate}
                                            onNameChange={handleFlowNameChange}
                                            onDescriptionChange={handleFlowDescriptionChange}
                                        />
                                    </Popover.Dropdown>
                                </Popover>
                            </SC.FlowCardActionWrapper>
                        )}
                    </SC.FlowCardHeader>
                    {/* <SC.FlowCardType style={{ color: workflowLabelMap[type].color }}>{workflowLabelMap[type].label}</SC.FlowCardType> */}
                    {/* <SC.FlowCardTitle>{name}</SC.FlowCardTitle> */}
                    <Tooltip title={description} maxWidth={240}>
                        <SC.FlowCardDescription>{description}</SC.FlowCardDescription>
                    </Tooltip>
                </SC.FlowCardContent>
                <SC.FlowCardFooter>
                    <SC.FlowCardFooterLeft onClick={ev => ev.stopPropagation()}>
                        <Switch
                            disabled={disabledWithVersion}
                            size="md"
                            color={workflowLabelMap[type].color}
                            checked={enabled}
                            onChange={ev => handleFlowEnabledChange(ev.target.checked)}
                        />
                        {unConfigured && (
                            <Tooltip placement="top" title="工作流无法正常运行，请检查配置">
                                <SC.FlowCardStatusWrapper>
                                    <SC.FlowCardStatus color="#D88987" type="WarningTriangle" size={18} />
                                </SC.FlowCardStatusWrapper>
                            </Tooltip>
                        )}
                    </SC.FlowCardFooterLeft>
                    {/* <SC.FlowCardUpdatedAt>{updatedAtString}</SC.FlowCardUpdatedAt> */}
                </SC.FlowCardFooter>
            </SC.FlowCardWrapper>
        </>
    )
}
